import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { Lead } from '../../types/lead';

interface LeadRowActionsMenuProps {
  lead: Lead;
  campaignId: string;
  canRunPhoneLookup: boolean;
  isAdmin: boolean;
  lookupBusy: boolean;
  lookingUp: boolean;
  onLookup: () => void;
  onDelete: () => void;
}

const MENU_WIDTH = 176;

const itemClass =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50';

const LeadRowActionsMenu: React.FC<LeadRowActionsMenuProps> = ({
  lead,
  campaignId,
  canRunPhoneLookup,
  isAdmin,
  lookupBusy,
  lookingUp,
  onLookup,
  onDelete,
}) => {
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const placeMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    const estimatedHeight = 168;
    const openUp = window.innerHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight;
    const top = openUp ? Math.max(8, rect.top - estimatedHeight - 6) : rect.bottom + 6;
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClose = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [open]);

  return (
    <div className="flex justify-end">
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Actions for ${lead.fullName || 'lead'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={lookingUp}
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          placeMenu();
          setOpen(true);
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
      >
        {lookingUp ? (
          <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <EllipsisVerticalIcon className="h-5 w-5" />
        )}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Lead actions"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="fixed z-[9999] overflow-hidden rounded-xl border border-border bg-card py-1 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {canRunPhoneLookup && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                disabled={lookupBusy}
                onClick={() => {
                  setOpen(false);
                  onLookup();
                }}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                Lookup
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={itemClass}
              onClick={() => {
                setOpen(false);
                navigate(`/campaigns/${campaignId}/leads/${lead.id}`);
              }}
            >
              <EyeIcon className="h-4 w-4" />
              View
            </button>
            {isAdmin && (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className={itemClass}
                  onClick={() => {
                    setOpen(false);
                    navigate(`/campaigns/${campaignId}/leads/${lead.id}/edit`);
                  }}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  Edit
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={`${itemClass} text-destructive hover:bg-destructive/10`}
                  onClick={() => {
                    setOpen(false);
                    onDelete();
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default LeadRowActionsMenu;
